import React, { useCallback, useEffect, useState } from "react";
import {
  AlertTriangle,
  Calendar,
  FileText,
  Inbox,
} from "lucide-react";
import Pagination from "../../../components/common/Pagination";
import InvoiceCreateModal from "./InvoiceCreateModal";
import InvoiceDetailModal from "./InvoiceDetailModal";
import InvoiceFilter from "./InvoiceFilter";
import InvoiceMiniDashboard from "./InvoiceMiniDashboard";
import InvoiceStatusModal from "./InvoiceStatusModal";
import InvoiceTypePickerModal from "./InvoiceTypePickerModal";
import InvoiceRowActionMenu from "./InvoiceRowActionMenu";
import {
  deleteInvoice,
  getAllInvoices,
  getStatusLabel,
} from "../../../api/finance/invoices/invoiceRequests";
import { showErrorToast, showSuccessToast } from "../../../components/ConfirmDialog";
import { confirmDelete, DELETE_ENTITY } from "../../../utils/confirmDelete";
import { formatDateVN } from "../../../utils/formatUtils";
import { currentPeriodDefaults } from "../../reports/finance/financePeriodUtils";

const todayYmd = () => {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${m}-${day}`;
};

/** Quá hạn khi có hạn TT, chưa hoàn tất/thanh toán, và hạn < hôm nay */
const isPaymentOverdue = (item) => {
  if (!item?.due_date) return false;
  const status = item.processing_status;
  if (status === "completed" || status === "paid") return false;
  const due = String(item.due_date).slice(0, 10);
  return due < todayYmd();
};

const formatMoney = (value) =>
  `${Number(value || 0).toLocaleString("vi-VN")} ₫`;

/** VAT % suy ra từ subtotal / vat_amount (giống form ghi nhận) */
const deriveVatPercent = (subtotal, vatAmount) => {
  const sub = Number(subtotal) || 0;
  const vat = Number(vatAmount) || 0;
  if (sub <= 0) return null;
  return Math.round((vat / sub) * 10000) / 100;
};

/** Số ngày còn lại đến hạn TT (âm = quá hạn). null nếu không áp dụng. */
const getDueCountdown = (item) => {
  if (!item?.due_date) return null;
  const status = item.processing_status;
  if (status === "completed" || status === "paid") return null;
  const due = String(item.due_date).slice(0, 10);
  const today = todayYmd();
  const dueMs = Date.parse(`${due}T00:00:00`);
  const todayMs = Date.parse(`${today}T00:00:00`);
  if (Number.isNaN(dueMs) || Number.isNaN(todayMs)) return null;
  return Math.round((dueMs - todayMs) / 86400000);
};

const InvoiceListPage = () => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState(() => {
    const period = currentPeriodDefaults();
    return {
      keyword: "",
      invoice_type: "",
      processing_status: "",
      branch_id: "",
      period_type: period.period_type,
      year: period.year,
      quarter: period.quarter,
      month: period.month,
      from_date: period.from_date,
      to_date: period.to_date,
    };
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [summary, setSummary] = useState({
    total_records: 0,
    total_amount: 0,
    purchase_count: 0,
    purchase_amount: 0,
    sale_count: 0,
    sale_amount: 0,
    pending_processing_count: 0,
    pending_processing_amount: 0,
  });

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showTypePicker, setShowTypePicker] = useState(false);
  const [createInvoiceType, setCreateInvoiceType] = useState("purchase");
  const [createSessionKey, setCreateSessionKey] = useState(0);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [statusInvoice, setStatusInvoice] = useState(null);
  const [detailId, setDetailId] = useState(null);
  const [openActionId, setOpenActionId] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await getAllInvoices({
        ...filters,
        page: currentPage,
        page_size: pageSize,
      });
      setRecords(res?.records || []);
      setTotalRecords(Number(res?.total || 0));
      setSummary(
        res?.summary || {
          total_records: 0,
          total_amount: 0,
          purchase_count: 0,
          purchase_amount: 0,
          sale_count: 0,
          sale_amount: 0,
          pending_processing_count: 0,
          pending_processing_amount: 0,
        }
      );
    } catch (error) {
      console.error("Load invoices failed:", error);
      showErrorToast(
        error?.response?.data?.message || "Không thể tải danh sách hóa đơn."
      );
    } finally {
      setLoading(false);
    }
  }, [filters, currentPage, pageSize]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filters]);

  const handleDelete = async (id, number) => {
    const result = await confirmDelete({
      type: DELETE_ENTITY.generic,
      name: number,
    });
    if (!result.isConfirmed) return;

    try {
      await deleteInvoice(id);
      showSuccessToast("Đã xóa hóa đơn.");
      fetchData();
    } catch (error) {
      console.error("Delete invoice failed:", error);
      showErrorToast(error?.response?.data?.message || "Không thể xóa hóa đơn.");
    }
  };

  const openCreateFlow = () => {
    setEditingInvoice(null);
    setShowTypePicker(true);
  };

  const openCreateWithType = (invoiceType = "purchase") => {
    setEditingInvoice(null);
    setCreateInvoiceType(invoiceType === "sale" ? "sale" : "purchase");
    setCreateSessionKey((k) => k + 1);
    setShowTypePicker(false);
    setShowCreateModal(true);
  };

  return (
    <div className="p-3 sm:p-4">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-5 md:mb-8">
        <div className="flex items-center gap-3 md:gap-4">
          <div className="bg-red-700 p-2 md:p-2.5 rounded-2xl text-white shadow-lg shadow-red-200">
            <FileText size={22} className="md:hidden" />
            <FileText size={24} className="hidden md:block" />
          </div>
          <div>
            <h1 className="text-lg md:text-xl font-black text-gray-800 tracking-tight uppercase">
              <span className="md:hidden">
                Hóa đơn
              </span>
              <span className="hidden md:inline">
                Quản lý <span className="text-red-700">Hóa đơn</span>
              </span>
            </h1>
            <div className="flex items-center gap-2 mt-0.5">
              <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
              <p className="text-[11px] text-gray-500 font-bold uppercase tracking-wider text-nowrap">
                <span className="text-red-600">{totalRecords}</span> hóa đơn đang
                hiển thị
              </p>
            </div>
          </div>
        </div>
      </div>

      <main>
        <InvoiceMiniDashboard summary={summary} />

        <InvoiceFilter
          onFilterChange={setFilters}
          onRefresh={fetchData}
          onNewClick={openCreateFlow}
        />

        {/* Mobile: card list — chỉ xem */}
        <div className="md:hidden space-y-3 relative min-h-[140px]">
          {loading && (
            <div className="absolute inset-0 bg-white/70 flex items-center justify-center z-10 rounded-2xl">
              <div className="animate-spin w-9 h-9 border-2 border-red-600 border-t-transparent rounded-full" />
            </div>
          )}
          {!loading && records.length === 0 && (
            <div className="bg-white rounded-2xl border-2 border-gray-300 px-4 py-12 text-center">
              <Inbox
                className="w-10 h-10 mx-auto text-gray-200 mb-2"
                strokeWidth={1}
              />
              <p className="text-gray-400 text-sm italic">
                Không có dữ liệu phù hợp bộ lọc.
              </p>
            </div>
          )}
          {records.map((item) => {
            const overdue = isPaymentOverdue(item);
            const isSale = item.invoice_type === "sale";
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => setDetailId(item.id)}
                className={`w-full text-left rounded-2xl border-2 shadow-sm p-4 active:scale-[0.99] transition-transform ${
                  isSale
                    ? "bg-emerald-50 border-emerald-300"
                    : "bg-red-50 border-red-300"
                }`}
              >
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="min-w-0">
                    <p className="text-sm font-black text-gray-800 uppercase tracking-tight truncate">
                      {item.invoice_number}
                    </p>
                    <p className="text-xs text-gray-700 mt-0.5 truncate font-medium">
                      {item.partner_name || "—"}
                    </p>
                    <p
                      className={`text-[10px] font-black mt-1 uppercase tracking-wide ${
                        isSale ? "text-emerald-700" : "text-red-700"
                      }`}
                    >
                      {isSale ? "HĐ bán" : "HĐ mua"}
                      {item.branch_name ? (
                        <span className="text-gray-500 font-bold">
                          {" "}
                          · {item.branch_name}
                        </span>
                      ) : null}
                    </p>
                  </div>
                  <StatusBadge
                    invoiceType={item.invoice_type}
                    status={item.processing_status}
                    compact
                  />
                </div>

                <div
                  className={`flex items-end justify-between gap-3 pt-3 border-t ${
                    isSale ? "border-emerald-200/80" : "border-red-200/80"
                  }`}
                >
                  <div className="min-w-0 space-y-1">
                    <p className="text-[10px] text-gray-500 font-bold flex items-center gap-1">
                      <Calendar size={10} className="shrink-0" />
                      <span>
                        HĐ:{" "}
                        <span className="text-gray-700">
                          {item.invoice_date
                            ? formatDateVN(item.invoice_date)
                            : "—"}
                        </span>
                      </span>
                    </p>
                    <p
                      className={`text-[10px] font-bold flex items-center gap-1 ${
                        overdue ? "text-red-600" : "text-gray-500"
                      }`}
                    >
                      {overdue ? (
                        <AlertTriangle size={10} className="shrink-0" />
                      ) : (
                        <Calendar size={10} className="shrink-0" />
                      )}
                      <span>
                        Hạn:{" "}
                        <span className={overdue ? "text-red-700" : "text-gray-700"}>
                          {item.due_date ? formatDateVN(item.due_date) : "—"}
                        </span>
                        {overdue ? (
                          <span className="ml-1 uppercase tracking-wider">
                            · Quá hạn
                          </span>
                        ) : null}
                      </span>
                    </p>
                  </div>
                  <p
                    className={`text-base font-black tabular-nums shrink-0 ${
                      isSale ? "text-emerald-700" : "text-red-700"
                    }`}
                  >
                    {Number(item.total_amount || 0).toLocaleString("vi-VN")} ₫
                  </p>
                </div>
              </button>
            );
          })}
        </div>

        {/* Desktop: table */}
        <div className="hidden md:block bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="overflow-x-auto relative min-h-[220px]">
            {loading && (
              <div className="absolute inset-0 bg-white/80 flex items-center justify-center z-10 rounded-xl">
                <div className="animate-spin w-10 h-10 border-2 border-red-600 border-t-transparent rounded-full" />
              </div>
            )}
            <table className="w-full text-left border-collapse min-w-[1180px]">
              <thead>
                <tr className="bg-gray-50/80 border-b border-gray-100">
                  <th className="px-4 py-4 text-[11px] font-black uppercase text-gray-400 text-center w-14">
                    STT
                  </th>
                  <th className="px-5 py-4 text-[11px] font-black uppercase text-gray-400">
                    Hóa đơn
                  </th>
                  <th className="px-5 py-4 text-[11px] font-black uppercase text-gray-400">
                    Loại / Đối tượng
                  </th>
                  <th className="px-4 py-4 text-[11px] font-black uppercase text-gray-400 whitespace-nowrap min-w-[9.5rem] text-right">
                    Trước VAT
                  </th>
                  <th className="px-4 py-4 text-[11px] font-black uppercase text-gray-400 whitespace-nowrap text-center w-24">
                    VAT (%)
                  </th>
                  <th className="px-4 py-4 text-[11px] font-black uppercase text-gray-400 whitespace-nowrap min-w-[9rem] text-right">
                    Giá trị VAT
                  </th>
                  <th className="px-5 py-4 text-[11px] font-black uppercase text-gray-400 whitespace-nowrap min-w-[10.5rem] text-right">
                    Tổng tiền
                  </th>
                  <th className="px-5 py-4 text-[11px] font-black uppercase text-gray-400 text-center whitespace-nowrap">
                    Hạn TT
                  </th>
                  <th className="px-5 py-4 text-[11px] font-black uppercase text-gray-400 text-left">
                    Trạng thái
                  </th>
                  <th
                    className="px-3 py-4 w-14 sticky right-0 z-20 bg-gray-50/95 border-l border-gray-100"
                    aria-label="Thao tác"
                  />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {!loading && records.length === 0 && (
                  <tr>
                    <td colSpan={10} className="px-6 py-16 text-center">
                      <Inbox
                        className="w-12 h-12 mx-auto text-gray-200 mb-3"
                        strokeWidth={1}
                      />
                      <p className="text-gray-400 text-sm font-medium italic">
                        Không có dữ liệu phù hợp bộ lọc.
                      </p>
                    </td>
                  </tr>
                )}
                {records.map((item, index) => {
                  const vatPct = deriveVatPercent(
                    item.subtotal,
                    item.vat_amount
                  );
                  return (
                  <tr
                    key={item.id}
                    className="group hover:bg-red-50/40 transition-colors cursor-pointer"
                    onClick={() => setDetailId(item.id)}
                  >
                    <td className="px-4 py-4 text-center text-xs font-bold text-gray-400">
                      {(currentPage - 1) * pageSize + index + 1}
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex items-start gap-2.5 min-w-0">
                        {item.thumbnail_url ? (
                          <img
                            src={item.thumbnail_url}
                            alt=""
                            className="h-11 w-9 shrink-0 rounded-md object-cover border border-red-100 bg-white shadow-sm"
                          />
                        ) : (
                          <FileText
                            size={16}
                            className="mt-0.5 shrink-0 text-gray-300 group-hover:text-red-400"
                          />
                        )}
                        <div className="min-w-0">
                          <p className="text-sm font-bold text-gray-800 group-hover:text-red-700 tracking-tight truncate">
                            {item.invoice_number}
                          </p>
                          <p className="mt-0.5 text-[11px] font-medium text-gray-500 flex items-center gap-1">
                            <Calendar size={11} className="shrink-0 text-gray-400" />
                            {item.invoice_date
                              ? formatDateVN(item.invoice_date)
                              : "—"}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-3">
                        <div className="h-9 w-9 rounded-xl bg-red-50 text-red-700 flex items-center justify-center text-xs font-black group-hover:bg-red-100 shrink-0">
                          {item.partner_name?.charAt(0)?.toUpperCase() || "?"}
                        </div>
                        <div className="min-w-0">
                          <div className="text-sm font-semibold text-gray-800 truncate">
                            {item.partner_name || "—"}
                          </div>
                          <div className="text-[10px] text-gray-500 font-medium">
                            {item.invoice_type === "purchase"
                              ? "Hóa đơn mua"
                              : "Hóa đơn bán"}
                            {item.branch_name ? ` · ${item.branch_name}` : ""}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4 text-right min-w-[9.5rem]">
                      <span className="text-sm font-semibold tabular-nums text-slate-700 whitespace-nowrap">
                        {formatMoney(item.subtotal)}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-center">
                      <span className="text-sm font-semibold tabular-nums text-slate-600">
                        {vatPct != null ? `${vatPct}%` : "—"}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-right min-w-[9rem]">
                      <span className="text-sm font-semibold tabular-nums text-slate-700 whitespace-nowrap">
                        {formatMoney(item.vat_amount)}
                      </span>
                    </td>
                    <td className="px-5 py-4 min-w-[10.5rem] text-right">
                      <div
                        className={`text-sm font-black tabular-nums whitespace-nowrap ${
                          item.invoice_type === "sale"
                            ? "text-emerald-600"
                            : "text-red-600"
                        }`}
                      >
                        {formatMoney(item.total_amount)}
                      </div>
                    </td>
                    <td className="px-5 py-4 text-center">
                      <DueDateCell item={item} />
                    </td>
                    <td className="px-5 py-4 text-left">
                      <StatusBadge
                        invoiceType={item.invoice_type}
                        status={item.processing_status}
                      />
                    </td>
                    <td
                      className="px-3 py-4 text-right sticky right-0 z-10 bg-white group-hover:bg-red-50/40 border-l border-gray-50"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="flex items-center justify-end">
                        <InvoiceRowActionMenu
                          item={item}
                          isOpen={openActionId === `invoice-${item.id}`}
                          onOpenChange={(open) =>
                            setOpenActionId(open ? `invoice-${item.id}` : null)
                          }
                          onView={(row) => setDetailId(row.id)}
                          onEdit={(row) => {
                            setEditingInvoice(row);
                            setShowCreateModal(true);
                          }}
                          onUpdateStatus={(row) => setStatusInvoice(row)}
                          onDelete={(row) =>
                            handleDelete(row.id, row.invoice_number)
                          }
                        />
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {totalRecords > 0 && (
          <div className="mt-4">
            <Pagination
              totalRecords={totalRecords}
              currentPage={currentPage}
              pageSize={pageSize}
              onPageChange={setCurrentPage}
              onPageSizeChange={(size) => {
                setCurrentPage(1);
                setPageSize(size);
              }}
            />
          </div>
        )}
      </main>

      <InvoiceTypePickerModal
        isOpen={showTypePicker}
        onClose={() => setShowTypePicker(false)}
        onSelect={openCreateWithType}
      />

      <InvoiceCreateModal
        key={
          editingInvoice?.id
            ? `edit-${editingInvoice.id}`
            : `create-${createSessionKey}`
        }
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        invoice={editingInvoice}
        defaultInvoiceType={createInvoiceType}
        onSaved={() => {
          setEditingInvoice(null);
          fetchData();
        }}
      />

      <InvoiceStatusModal
        isOpen={!!statusInvoice}
        onClose={() => setStatusInvoice(null)}
        invoice={statusInvoice}
        onSaved={fetchData}
      />

      <InvoiceDetailModal
        isOpen={!!detailId}
        onClose={() => setDetailId(null)}
        invoiceId={detailId}
      />
    </div>
  );
};

const StatusBadge = ({ invoiceType, status, compact = false }) => {
  const label = getStatusLabel(invoiceType, status);
  const isDone = status === "completed";
  const isPending = status === "pending" || status === "issued";

  let cls = compact
    ? "shrink-0 max-w-[7.5rem] truncate px-2 py-0.5 rounded-full text-[10px] font-semibold border "
    : "inline-flex px-2.5 py-1 rounded-full text-xs font-medium border ";
  if (isDone) cls += "bg-emerald-50 text-emerald-700 border-emerald-100";
  else if (isPending) cls += "bg-amber-50 text-amber-700 border-amber-100";
  else cls += "bg-blue-50 text-blue-700 border-blue-100";

  return (
    <span className={cls} title={label}>
      {label}
    </span>
  );
};

/** Ngày hạn TT + badge đếm ngày / cảnh báo */
const DueDateCell = ({ item }) => {
  if (!item?.due_date) {
    return <span className="text-xs text-gray-400">—</span>;
  }

  const days = getDueCountdown(item);
  const overdue = days != null && days < 0;
  const dueSoon = days != null && days >= 0 && days <= 7;

  let badge = null;
  if (days != null) {
    if (days < 0) {
      badge = {
        text: `Quá hạn ${Math.abs(days)} ngày`,
        cls: "bg-red-100 text-red-700 border-red-200",
        icon: true,
      };
    } else if (days === 0) {
      badge = {
        text: "Đến hạn hôm nay",
        cls: "bg-amber-100 text-amber-800 border-amber-200",
        icon: true,
      };
    } else if (days <= 7) {
      badge = {
        text: `Còn ${days} ngày`,
        cls: "bg-amber-50 text-amber-700 border-amber-200",
        icon: false,
      };
    } else {
      badge = {
        text: `Còn ${days} ngày`,
        cls: "bg-slate-100 text-slate-600 border-slate-200",
        icon: false,
      };
    }
  }

  return (
    <div className="inline-flex flex-col items-center gap-1.5">
      <div
        className={`inline-flex items-center gap-1.5 text-xs font-bold px-2 py-1 rounded-lg border ${
          overdue
            ? "text-red-700 bg-red-50 border-red-200"
            : dueSoon
              ? "text-amber-800 bg-amber-50 border-amber-200"
              : "text-gray-600 bg-gray-50 border-transparent group-hover:bg-white group-hover:border-red-100"
        }`}
      >
        {overdue || (days === 0) ? (
          <AlertTriangle
            size={12}
            className={overdue ? "text-red-500" : "text-amber-600"}
          />
        ) : (
          <Calendar size={12} className="text-gray-400" />
        )}
        {formatDateVN(item.due_date)}
      </div>
      {badge ? (
        <span
          className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold ${badge.cls}`}
        >
          {badge.icon ? <AlertTriangle size={10} /> : null}
          {badge.text}
        </span>
      ) : null}
    </div>
  );
};

export default InvoiceListPage;
